/// <amd-module name="Analytics/Common/Utilities/AnalyticsExceptionUtilities" />

import * as AnalyticsResources from "Analytics/Common/Resources";

export enum AnalyticsExceptionType {
    DataNotReady,
    Unknown,
    MissingAnalyticsPermissions,
    Disabled
}

/**
 * recognizes errors from analytics and maps them to expected exceptions
 * @param e exception returned by the oData client.
 * @returns a enum indicating if the data not ready scenario was handled.
 */
export function recognizeAnalyticsException(e: any): AnalyticsExceptionType {
    const analyticsDeletingErrorId = "VS403516";
    const analyticsNotReadyErrorId = "VS403280";
    const analyticsPausedErrorId = "VS403515";
    const analyticsSyncingErrorId = "VS403454";
    const axPermsDeniedInAProjectErrorId = "VS403496";
    const axPermsDeniedInThisProjectErrorId = "VS403490";
    const axFeatureDisabledErrorId = "VS403514";

    const extractMessage = (e: any) => {
        return ((e.serverError || {}).error || {}).message || (e.serverError || {}).message;
    };
    const messageToScan = extractMessage(e);

    // we treat both cases the same from the UI perspective
    const checkHasNotReadyMessage = (message: string) => {
        return message.search(analyticsNotReadyErrorId) >= 0 || message.search(analyticsSyncingErrorId) >= 0;
    };
    const isFailedGetMessage = e.status === 503 && checkHasNotReadyMessage(messageToScan);
    const isFailedBatchMessage = e.error && checkHasNotReadyMessage(e.error.message);

    let result: AnalyticsExceptionType;
    if (isFailedGetMessage || isFailedBatchMessage) {
        result = AnalyticsExceptionType.DataNotReady;
    } else if (
        messageToScan != null &&
        (messageToScan.search(axPermsDeniedInAProjectErrorId) >= 0 || messageToScan.search(axPermsDeniedInThisProjectErrorId) >= 0)
    ) {
        result = AnalyticsExceptionType.MissingAnalyticsPermissions;
    } else if (
        messageToScan != null &&
        (messageToScan.search(axFeatureDisabledErrorId) >= 0 ||
            messageToScan.search(analyticsPausedErrorId) >= 0 ||
            messageToScan.search(analyticsDeletingErrorId) >= 0)
    ) {
        result = AnalyticsExceptionType.Disabled;
    } else {
        result = AnalyticsExceptionType.Unknown;
    }

    return result;
}

/**
 * Uses heuristics to locate error message first by ODataError pattern, with fallback strategies for generic client failures.
 */
export function extractODataError(arg: any): string {
    let errorMessage;

    // If detailed error message is not sent by the stack, fall back to human-readable message instead of just zero.
    // This happens when browser can't reach server
    if (arg && arg.status !== "undefined" && arg.status === 0) {
        errorMessage = AnalyticsResources.ConnectionErrorText;
    } else {
        // Try to interpret as an ODataError
        if (isODataError(arg)) {
            errorMessage = arg.error.message;
        } else if (arg && arg.responseText) {
            try {
                const parsedResponse = JSON.parse(arg.responseText);
                if (isODataError(parsedResponse)) {
                    errorMessage = parsedResponse.error.message;
                }
            } catch (e) {
                // Do nothing so we resort to the fallback parser
            }
        }

        // Fallback to interpreting
        if (errorMessage == null) {
            errorMessage = extractError(arg);
        }
    }

    return errorMessage;
}

/*
 * Given an error object returns string component of that error
 */
function extractError(error: any): string {
    if (error == null) {
        return AnalyticsResources.NoErrorText;
    } else if (error.message) {
        return error.message;
    } else if (error.responseText) {
        return error.responseText;
    } else if (typeof error === "string") {
        return error;
    } else {
        return AnalyticsResources.NoErrorText;
    }

    return JSON.stringify(error);
}

function isODataError(arg: any): arg is ODataError {
    return arg != null && typeof arg.error !== "undefined" && typeof arg.error.code !== "undefined" && typeof arg.error.message !== "undefined";
}

/**
 * An error returned by OData v4.
 * http://docs.oasis-open.org/odata/odata-json-format/v4.0/os/odata-json-format-v4.0-os.html#_Toc372793091
 * The error response must be a single JSON object. This object must have a single name/value pair named error. The value must be a JSON object.
 * This object must contain name/value pairs with the names code and message, and it may contain name/value pairs with the names target, details and innererror.
 */
interface ODataError {
    error: {
        /** A service-defined error code. This code serves as a sub-status for the HTTP error code specified in the response. */
        code: string;
        /** A human-readable representation of the error. */
        message: string;
    };
}
